import { action, observable } from 'mobx'
import BrandingService from '@modules/wholesale/brandingSetting/services/BrandingService'
import { IBrandImage } from '@modules/wholesale/brandingSetting'
import { computedFn } from 'mobx-utils'
import set from 'lodash/set'
import { config } from '@config'

export class BrandingStore {
  @observable images: IBrandImage[] = []
  defaultConfiguration: any = {
    landingPage: {
      backgroundImage: {
        imageUrl: ''
      },
      logoImage: {
        imageUrl: ''
      }
    },
    selectionPage: {
      backgroundImage: {
        imageUrl: ''
      },
      logoImage: {
        imageUrl: ''
      }
    },
    buyingBoardApplication: {
      logoImage: {
        imageUrl: ''
      }
    },
    showroomApplication: {
      logoImage: {
        imageUrl: ''
      }
    },
    pdf: {
      logoImage: {
        imageUrl: ''
      }
    }
  }
  @observable openDeleteConfirmation = false
  @observable configuration: any = this.defaultConfiguration
  private brandingService = new BrandingService()

  private isValid = value => {
    return !!value || value.trim().length > 0
  }

  @action
  fetchConfiguration = async () => {
    try {
      const latestConfiguration = await this.brandingService.getConfiguration()
      this.setConfiguration(latestConfiguration)
    } catch (e) {
      console.log('Failed to fetch configuration', e)
      throw e
    }
    return this.configuration
  }

  @action
  updateConfiguration = async configuration => {
    try {
      const updatedConfiguration = await this.brandingService.updateConfiguration(
        configuration
      )
      this.configuration = updatedConfiguration
    } catch (e) {
      console.log('Failed to update configuration', e)
      throw e
    }
    return this.configuration
  }

  getImages = async () => {
    try {
      const images = await this.brandingService.getImages()
      this.images = images
    } catch (e) {
      console.log('Failed to fetch images', e)
      throw e
    }
    return this.images
  }
  @action
  uploadImage = async file => {
    try {
      const uploadedImage = await this.brandingService.uploadImage(file)
      return uploadedImage
    } catch (e) {
      console.log('Failed to update media', e)
      throw e
    }
  }

  @action
  deleteImage = async (image: IBrandImage) => {
    try {
      await this.brandingService.deleteImage(image.key)
      // reset in use keys to default img
      this.getImageKeysInUse(image).map(key => set(this.configuration, key, ''))
      await this.updateConfiguration(this.configuration)
    } catch (e) {
      console.log('Failed to delete media', e)
      throw e
    }
  }

  getImageKeysInUse = computedFn((image: IBrandImage) => {
    const keysInUse = []
    if (this.configuration?.landingPage?.backgroundImage?.imageUrl === image.imageUrl) {
      keysInUse.push('landingPage.backgroundImage.imageUrl')
    }
    if (this.configuration?.landingPage?.logoImage?.imageUrl === image.imageUrl) {
      keysInUse.push('landingPage.logoImage.imageUrl')
    }
    if (this.configuration?.selectionPage?.backgroundImage?.imageUrl === image.imageUrl) {
      keysInUse.push('selectionPage.backgroundImage.imageUrl')
    }
    if (this.configuration?.selectionPage?.logoImage?.imageUrl === image.imageUrl) {
      keysInUse.push('selectionPage.logoImage.imageUrl')
    }
    if (
      this.configuration?.buyingBoardApplication?.logoImage?.imageUrl === image.imageUrl
    ) {
      keysInUse.push('buyingBoardApplication.logoImage.imageUrl')
    }
    if (this.configuration?.showroomApplication?.logoImage?.imageUrl === image.imageUrl) {
      keysInUse.push('showroomApplication.logoImage.imageUrl')
    }
    if (this.configuration?.pdf?.logoImage?.imageUrl === image.imageUrl) {
      keysInUse.push('pdf.logoImage.imageUrl')
    }
    return keysInUse
  })

  @action
  openDeleteConfirmationDialog = () => (this.openDeleteConfirmation = true)

  @action
  closeDeleteConfirmationDialog = () => (this.openDeleteConfirmation = false)

  setConfiguration = configuration => {
    this.configuration = this.defaultConfiguration
    if (this.isValid(configuration?.landingPage?.backgroundImage?.imageUrl)) {
      this.configuration.landingPage.backgroundImage.imageUrl =
        configuration?.landingPage?.backgroundImage?.imageUrl
    }
    if (this.isValid(configuration.landingPage.logoImage?.imageUrl)) {
      this.configuration.landingPage.logoImage.imageUrl =
        configuration.landingPage.logoImage.imageUrl
    }

    if (this.isValid(configuration?.selectionPage?.backgroundImage?.imageUrl)) {
      this.configuration.selectionPage.backgroundImage.imageUrl =
        configuration.selectionPage?.backgroundImage?.imageUrl
    }
    if (this.isValid(configuration?.selectionPage?.logoImage?.imageUrl)) {
      this.configuration.selectionPage.logoImage.imageUrl =
        configuration.selectionPage.logoImage.imageUrl
    }

    if (this.isValid(configuration?.buyingBoardApplication?.logoImage?.imageUrl)) {
      this.configuration.buyingBoardApplication.logoImage.imageUrl =
        configuration?.buyingBoardApplication?.logoImage?.imageUrl
    }

    if (this.isValid(configuration?.showroomApplication?.logoImage?.imageUrl)) {
      this.configuration.showroomApplication.logoImage.imageUrl =
        configuration.showroomApplication?.logoImage?.imageUrl
    }
    if (this.isValid(configuration?.pdf?.logoImage?.imageUrl)) {
      this.configuration.pdf.logoImage.imageUrl = configuration.pdf?.logoImage?.imageUrl
    }
  }

  getMediaURI = computedFn(
    mediaURL => `${config.buyingBoardServiceRestEndpoint}${mediaURL}`
  )
}

const store = new BrandingStore()
export default store
